import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { inject, Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map, tap } from 'rxjs/operators';
import * as fromRouter from 'app/reducers/router';
import * as assetsSapActions from 'app/actions/sap-assets';
import * as profileActions from 'app/actions/profile';
import { concatLatestFrom } from '@ngrx/operators';
import { UrlUtils } from '../utils/url-utils';
import { assetsActions } from '../actions/assets';
import { AssetSelected } from '../models/api/asset-tree';
import { appRouterActions } from '../actions/router';
import { ActivatedRoute, Params, Router } from '@angular/router';

@Injectable()
export class RouterEffects {
  private actions$: Actions = inject(Actions);
  private store: Store = inject(Store);
  private router: Router = inject(Router);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);

  navigationEnd$ = createEffect(() => this.actions$.pipe(
    ofType(ROUTER_NAVIGATED),
    concatLatestFrom(() => this.store.select(fromRouter.getRouteUrl)),
    map(([, url]: [Action, string]) => profileActions.checkIsExportPollingAllowed({ url }))
  ));

  navigationEndCheckForStoringRegularAssetSelection$ = createEffect(() => this.actions$.pipe(
    ofType(ROUTER_NAVIGATED),
    concatLatestFrom(() => [
      this.store.select(fromRouter.getRouteUrl),
      this.store.select(fromRouter.getIsRegularAssetZoneActivated),
      this.store.select(fromRouter.getIsRegularAssetZoneActivatedForPreviousUrl),
    ]),
    filter(([, url, isRegularAssetZone, isRegularAssetZoneForPreviousUrl]) =>
      this.checkWhetherWeNeedToStoreAssetSelected(url, isRegularAssetZone, isRegularAssetZoneForPreviousUrl)
    ),
    concatLatestFrom(() => [
      this.store.select(fromRouter.getAssetNodeId),
      this.store.select(fromRouter.getAssetId)
    ]),
    map(([[], nodeId, assetId]): AssetSelected => ({ assetId, nodeId })),
    map((selected: AssetSelected) =>
      assetsActions.storeSelectedRegularNode({ selected })
    )
  ));

  navigationEndCheckForStoringSapAssetSelection$ = createEffect(() => this.actions$.pipe(
    ofType(ROUTER_NAVIGATED),
    concatLatestFrom(() => [
      this.store.select(fromRouter.getRouteUrl),
      this.store.select(fromRouter.getIsSapAssetZoneActivated),
      this.store.select(fromRouter.getIsSapAssetZoneActivatedForPreviousUrl),
    ]),
    filter(([, url, isSapAssetZone, isSapAssetZoneForPreviousUrl]) =>
      this.checkWhetherWeNeedToStoreAssetSelected(url, isSapAssetZone, isSapAssetZoneForPreviousUrl)
    ),
    concatLatestFrom(() => this.store.select(fromRouter.selectedSapAsset)),
    map(([[], selected]) =>
      assetsSapActions.storeSelectedSapNode({ selected })
    )
  ));

  updateQueryParamsWithNewValue$ = createEffect(() => this.actions$.pipe(
    ofType(appRouterActions.updateQueryParamsWithNewValue),
    map(action => action.query),
    concatLatestFrom(() => this.store.select(fromRouter.getRouteQueryParams)),
    tap(([actionQuery, queryParams]: [Params, Params]) => {
      this.router.navigate([], {
        queryParams: { ...queryParams, ...actionQuery },
        relativeTo: this.activatedRoute,
        queryParamsHandling: 'merge',
      });
    }),
  ), { dispatch: false });

  private checkWhetherWeNeedToStoreAssetSelected(
    url: string,
    isAssetZone: boolean,
    isAssetZoneForPreviousUrl: boolean,
  ): boolean {
    const primaryUrl: string = UrlUtils.getPrimaryRouterUrl(url);

    return primaryUrl?.includes('machines') && isAssetZone && isAssetZoneForPreviousUrl;
  }
}
